import { conditionalString, DateFormats, formatDate } from '@strata/helper-functions';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import * as Yup from 'yup';

import {
    type ClaimDetails,
    useGetClaimDetailsQuery,
    usePutSnoozeClaimMutation,
} from 'store/api/medbillingApi';

const Title = 'Snooze Research';

const ReasonOptions = [
    'Pending Payer Reprocessing',
    'Pending Payer Research',
    'Pending Pharmacy Response',
];

const minDate = dayjs().startOf('day').add(1, 'day').toDate();

const ValidationSchema = Yup.object({
    ...ReasonInput.validationSchema,
    snoozeFollowUpDate: DateInputFormik.validator()
        .min(minDate, 'Must be future date.')
        .required(validationStrings.requiredField.errorMessage),
});

const getInitialValues = (claimDetails?: ClaimDetails) => {
    const snoozeReason = claimDetails?.snoozeReason;
    const isOtherReason = snoozeReason && !ReasonOptions.includes(snoozeReason);
    return {
        snoozeFollowUpDate: claimDetails?.snoozeFollowUpDate as Date | undefined,
        reason: isOtherReason ? 'Other' : snoozeReason,
        reasonOther: isOtherReason ? snoozeReason : undefined,
    } as Yup.InferType<typeof ValidationSchema>;
};

interface SnoozeResearchDialogProps {
    onClose: () => void;
    payerId: string;
    transactionId: string;
    isOpen?: boolean;
    gridAction?: boolean;
}

export const SnoozeResearchDialog = ({
    onClose,
    payerId,
    transactionId,
    isOpen = true,
    gridAction = false,
}: SnoozeResearchDialogProps) => {
    const { data: claimDetails } = useGetClaimDetailsQuery({
        payerId,
        transactionId,
    });
    const [putSnoozeClaim] = usePutSnoozeClaimMutation();
    return (
        <Formik
            initialValues={getInitialValues(claimDetails)}
            enableReinitialize
            validateOnMount
            validationSchema={ValidationSchema}
            onSubmit={async (values, { resetForm }) => {
                await putSnoozeClaim({
                    payerId,
                    transactionId,
                    ...ReasonInput.submitValues(values),
                    snoozeFollowUpDate: formatDate(
                        values.snoozeFollowUpDate,
                        DateFormats.HYPHEN_DATE,
                    ),
                }).unwrap();

                SuccessToast({
                    message: `Claim Research was Snoozed successfully.${conditionalString(gridAction, ' Based on filters this claim may have disappeared from view.')}`,
                });
                resetForm();
                onClose();
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={560}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel={Title}
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <>
                            <ReasonInput reasons={ReasonOptions} hideSource />
                            <DateInputFormik
                                id="snoozeFollowUpDate"
                                label="Set Follow-Up Reminder On"
                                minDate={minDate}
                                required
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

SnoozeResearchDialog.Title = Title;
